import React, { useEffect, useRef, useState } from "react";
import {
  ArrowDownCircle,
  ArrowUpCircle,
  Calendar,
  ChevronDown,
  DollarSign,
  Eraser,
  RotateCw,
  Search,
  SlidersHorizontal,
} from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useDebounce } from "use-debounce";
import { hasPermission } from "../../../utils/permissionUtils";
import { getAllBranches } from "../../../api/hrm/branches/info";
import MobileAdvancedFilterSheet from "../../../components/ui/MobileAdvancedFilterSheet";

const defaultDateRange = () => {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1)
    .toISOString()
    .slice(0, 10);
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0)
    .toISOString()
    .slice(0, 10);
  return { from_date: firstDay, to_date: lastDay };
};

const DIRECTION_OPTIONS = [
  { value: "", label: "Tất cả" },
  { value: "in", label: "Thu" },
  { value: "out", label: "Chi" },
];

const IncomeExpenditureFilter = ({
  onFilterChange,
  onRefresh,
  onCreateReceipt,
  onCreatePayment,
  onCreateCustomerReceipt,
}) => {
  const [advanced, setAdvanced] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [branchOptions, setBranchOptions] = useState([]);

  const [filters, setFilters] = useState({
    keyword: "",
    direction: "",
    branch_id: "",
    ...defaultDateRange(),
  });

  const [debouncedKeyword] = useDebounce(filters.keyword, 400);

  const isFiltering =
    filters.keyword !== "" ||
    filters.direction !== "" ||
    filters.branch_id !== "";

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    getAllBranches()
      .then((res) => {
        const items = Array.isArray(res) ? res : res?.items || res?.data || [];
        setBranchOptions(
          items.map((b) => ({ value: b.id, label: b.name || "—" }))
        );
      })
      .catch(() => setBranchOptions([]));
  }, []);

  useEffect(() => {
    onFilterChange?.({ ...filters, keyword: debouncedKeyword });
  }, [debouncedKeyword]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateFilter = (name, value) => {
    const updated = { ...filters, [name]: value };
    setFilters(updated);
    if (name !== "keyword") onFilterChange?.(updated);
  };

  const clearFilters = () => {
    const cleared = {
      keyword: "",
      direction: "",
      branch_id: "",
      ...defaultDateRange(),
    };
    setFilters(cleared);
    onFilterChange?.(cleared);
  };

  const ActionButton = ({ icon: Icon, onClick, title, active }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`h-11 w-11 flex items-center justify-center rounded-xl border transition-all duration-200 ${
        active
          ? "bg-red-50 border-red-200 text-red-600 shadow-sm"
          : "bg-white border-gray-200 text-gray-500 hover:border-red-400 hover:text-red-500"
      }`}
    >
      <Icon size={18} />
    </button>
  );

  const canRecord = hasPermission("finance.income_expenditure", "C");

  const RecordInButton = ({ compact = false }) =>
    canRecord ? (
      <button
        type="button"
        onClick={onCreateReceipt}
        className={`inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-emerald-600 to-emerald-700 text-white font-bold shadow-lg shadow-emerald-200 hover:from-emerald-700 hover:to-emerald-800 transition-all ${
          compact ? "h-11 px-3" : "h-11 px-5"
        }`}
      >
        <ArrowDownCircle size={18} />
        {!compact && <span className="text-sm">Ghi nhận thu</span>}
      </button>
    ) : null;

  const CustomerReceiptButton = ({ compact = false }) =>
    canRecord ? (
      <button
        type="button"
        onClick={onCreateCustomerReceipt}
        className={`inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 text-white font-bold shadow-lg shadow-amber-200 hover:from-amber-600 hover:to-amber-700 transition-all ${
          compact ? "h-11 px-3" : "h-11 px-5"
        }`}
      >
        <DollarSign size={18} />
        {!compact && <span className="text-sm">Phiếu thu KH</span>}
      </button>
    ) : null;

  const RecordOutButton = ({ compact = false }) =>
    canRecord ? (
      <button
        type="button"
        onClick={onCreatePayment}
        className={`inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-red-600 to-red-700 text-white font-bold shadow-lg shadow-red-200 hover:from-red-700 hover:to-red-800 transition-all ${
          compact ? "h-11 px-3" : "h-11 px-5"
        }`}
      >
        <ArrowUpCircle size={18} />
        {!compact && <span className="text-sm">Ghi nhận chi</span>}
      </button>
    ) : null;

  const SelectBox = ({ label, value, options, onChange, placeholder }) => (
    <div className="flex flex-col gap-1.5 w-full">
      <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1">
        {label}
      </label>
      <div className="relative group">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="appearance-none w-full bg-white border border-gray-200 text-gray-700 py-3 px-4 pr-10 rounded-2xl text-sm
                     focus:outline-none focus:border-red-500 focus:ring-4 focus:ring-red-500/5 transition-all cursor-pointer font-medium shadow-sm"
        >
          <option value="">{placeholder || `Tất cả ${label}`}</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <ChevronDown
          size={16}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none group-hover:text-red-500 transition-colors"
        />
      </div>
    </div>
  );

  const DateBox = ({ label, value, onChange }) => (
    <div className="flex flex-col gap-1.5 w-full">
      <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1">
        {label}
      </label>
      <div className="relative group">
        <Calendar
          size={16}
          className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
        />
        <input
          type="date"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-white border border-gray-200 py-3 pl-11 pr-4 rounded-2xl text-sm font-medium outline-none focus:border-red-500 focus:ring-4 focus:ring-red-500/5"
        />
      </div>
    </div>
  );

  const AdvancedFields = () => (
    <>
      <SelectBox
        label="Loại dòng"
        value={filters.direction}
        options={DIRECTION_OPTIONS.filter((o) => o.value !== "")}
        onChange={(v) => updateFilter("direction", v)}
        placeholder="Tất cả loại dòng"
      />
      <SelectBox
        label="Chi nhánh"
        value={filters.branch_id}
        options={branchOptions}
        onChange={(v) => updateFilter("branch_id", v)}
      />
      <DateBox
        label="Từ ngày"
        value={filters.from_date}
        onChange={(v) => updateFilter("from_date", v)}
      />
      <DateBox
        label="Đến ngày"
        value={filters.to_date}
        onChange={(v) => updateFilter("to_date", v)}
      />
    </>
  );

  if (isMobile) {
    return (
      <div className="mb-6 space-y-4">
        <div className="flex gap-2">
          <div className="relative flex-1 group">
            <Search
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors"
              size={18}
            />
            <input
              type="text"
              placeholder="Tìm mã phiếu, đối tượng..."
              value={filters.keyword}
              onChange={(e) => updateFilter("keyword", e.target.value)}
              className="w-full bg-white border border-gray-200 pl-11 pr-4 py-3 rounded-2xl focus:outline-none focus:border-red-500 shadow-sm transition-all"
            />
          </div>
          <ActionButton
            icon={SlidersHorizontal}
            onClick={() => setAdvanced(!advanced)}
            active={advanced}
            title="Lọc nâng cao"
          />
          <ActionButton
            icon={RotateCw}
            onClick={() =>
              onRefresh
                ? onRefresh()
                : onFilterChange?.({ ...filters, keyword: debouncedKeyword })
            }
            title="Làm mới"
          />
          <RecordInButton compact />
          <CustomerReceiptButton compact />
          <RecordOutButton compact />
        </div>

        <MobileAdvancedFilterSheet
          open={advanced}
          onClose={() => setAdvanced(false)}
          title="Bộ lọc nâng cao"
          footer={
            <>
              <button
                type="button"
                onClick={clearFilters}
                className="flex-1 py-4 border border-gray-200 text-gray-700 rounded-2xl font-bold"
              >
                XÓA LỌC
              </button>
              <button
                type="button"
                onClick={() => setAdvanced(false)}
                className="flex-1 py-4 bg-red-600 text-white rounded-2xl font-bold shadow-lg shadow-red-200"
              >
                ÁP DỤNG
              </button>
            </>
          }
        >
          <div className="grid grid-cols-1 gap-4">
            <AdvancedFields />
          </div>
        </MobileAdvancedFilterSheet>
      </div>
    );
  }

  return (
    <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm mb-6">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative w-full max-w-sm group">
          <Search
            className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors"
            size={18}
          />
          <input
            type="text"
            placeholder="Tìm mã phiếu, đối tượng..."
            value={filters.keyword}
            onChange={(e) => updateFilter("keyword", e.target.value)}
            className="w-full bg-gray-50 border-transparent border-2 focus:bg-white focus:border-red-500 pl-11 pr-4 py-2.5 rounded-2xl outline-none transition-all font-medium text-gray-700"
          />
        </div>

        <ActionButton
          icon={RotateCw}
          onClick={() => (onRefresh ? onRefresh() : onFilterChange?.({ ...filters, keyword: debouncedKeyword }))}
          title="Làm mới"
        />
        <ActionButton
          icon={SlidersHorizontal}
          onClick={() => setAdvanced(!advanced)}
          active={advanced}
          title="Lọc nâng cao"
        />

        {isFiltering && (
          <button
            type="button"
            onClick={clearFilters}
            className="text-xs font-bold text-red-500 hover:text-red-700 flex items-center gap-1 uppercase tracking-wider transition-all ml-2"
          >
            <Eraser size={14} /> Xóa lọc
          </button>
        )}

        <div className="flex items-center gap-2 ml-auto">
          <RecordInButton />
          <CustomerReceiptButton />
          <RecordOutButton />
        </div>
      </div>

      <AnimatePresence>
        {advanced && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-gray-100">
              <AdvancedFields />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default IncomeExpenditureFilter;
